import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../components/ui/spinner";
import AccessLevelSection from "./AccessLevelSection";
import DrawerHeader from "./DrawerHeader";
import PermissionsSection from "./PermissionsSection";
import {
  openOAuthPopup,
  settleOAuthPopup,
  watchOAuthPopupCompletion,
} from "../oauth";
import { useMCPConnection } from "../hooks/useMCPConnection";
import { useMCPConnectionTest } from "../hooks/useMCPConnectionTest";
import { MCP_CONNECTIONS_QUERY_KEY } from "../hooks/useMCPConnections";
import { useMCPTools } from "../hooks/useMCPTools";
import { useReconnectMCPConnection } from "../hooks/useReconnectMCPConnection";
import { useUpdateMCPConnection } from "../hooks/useUpdateMCPConnection";
import { canManageMcpConnection } from "../connectionAccess";
import type { OrgUser } from "../hooks/useUsers";

type Props = {
  open: boolean;
  onClose: () => void;
  connectionId: string | null;
  currentUser: OrgUser | undefined;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

const DRAWER_PAPER_SX = {
  width: { xs: "100%", sm: 600 },
  backgroundColor: "var(--card)",
  backgroundImage: "none",
} as const;

const MCPDetailDrawer = ({ open, onClose, connectionId, currentUser, onEdit, onDelete }: Props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const queryClient = useQueryClient();
  const { connection, dto, isLoading, isFetching, error } = useMCPConnection(connectionId);
  // Only probe while the drawer is open so closing the drawer doesn't keep
  // firing pointless POSTs in the background.
  const testTargetId = open ? connectionId : null;
  const {
    status: liveStatus,
    errorMessage: testError,
    authRequired,
    isTesting,
    refetch: rerunTest,
  } = useMCPConnectionTest(testTargetId);
  const {
    tools,
    isLoading: toolsLoading,
    error: toolsError,
  } = useMCPTools(open ? connectionId : null);
  const updateMutation = useUpdateMCPConnection();
  const reconnectMutation = useReconnectMCPConnection();

  const isActive = dto?.is_active ?? true;
  // The list response is only a rendering seed. Use the authoritative detail
  // response for drawer actions, and fail closed while that response loads.
  const canManage =
    !isFetching && canManageMcpConnection(dto?.sharing, currentUser);

  const handleToggleActive = useCallback(() => {
    if (!connectionId) return;
    updateMutation.mutate({
      id: connectionId,
      payload: { is_active: !isActive },
    });
  }, [connectionId, isActive, updateMutation]);

  const handleRefresh = useCallback(() => {
    rerunTest();
  }, [rerunTest]);

  const handleReconnect = useCallback(() => {
    if (!connectionId) return;
    // Open the tab synchronously on click so the popup blocker allows it.
    const popup = openOAuthPopup();
    reconnectMutation.mutate(connectionId, {
      onSuccess: (init) => {
        const { redirected, blocked } = settleOAuthPopup(
          popup,
          init.authorization_url,
        );
        if (blocked && init.authorization_url) {
          toast.warning(
            t("mcpPage.toasts.popupBlocked", { url: init.authorization_url }),
          );
        } else if (redirected) {
          watchOAuthPopupCompletion(popup!, init.authorization_url, () => {
            queryClient.invalidateQueries({
              queryKey: MCP_CONNECTIONS_QUERY_KEY,
            });
          });
          toast.success(t("mcpPage.toasts.completeAuthorization"));
        }
      },
      onError: () => popup?.close(),
    });
  }, [connectionId, queryClient, reconnectMutation, t]);

  return (
    <Drawer
      anchor={isRTL ? "left" : "right"}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: DRAWER_PAPER_SX }}
    >
      {isLoading && !connection ? (
        <div className="flex h-full items-center justify-center">
          <Spinner className="size-5 text-muted-foreground" />
        </div>
      ) : error && !connection ? (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">
          {t("mcpPage.errors.loadDetails")}
        </div>
      ) : connection ? (
        <div className="flex h-full flex-col overflow-y-auto">
          <DrawerHeader
            connection={connection}
            status={liveStatus}
            authRequired={authRequired}
            errorMessage={testError}
            isActive={isActive}
            isTogglingActive={updateMutation.isPending}
            onToggleActive={handleToggleActive}
            onRefresh={handleRefresh}
            isRefreshing={isTesting}
            canManage={canManage}
            onEdit={() => onEdit(connection.id)}
            onDelete={() => onDelete(connection.id)}
            isOAuth={dto?.auth_type === "oauth"}
            onReconnect={handleReconnect}
            isReconnecting={reconnectMutation.isPending}
          />

          {connection.description?.trim() && (
            <p className="px-4 py-4 text-sm leading-relaxed text-foreground">
              {connection.description}
            </p>
          )}

          <PermissionsSection
            connectionId={connection.id}
            tools={tools}
            isLoading={toolsLoading}
            errorMessage={toolsError ? t("mcpPage.errors.loadTools") : null}
          />
          <AccessLevelSection
            connectionId={connection.id}
            sharing={dto?.sharing}
          />
        </div>
      ) : null}
    </Drawer>
  );
};

export default MCPDetailDrawer;
